import React, { useEffect, useState, useCallback } from 'react';
import { adminApi } from '../../services/api';
import toast from 'react-hot-toast';
import { Plus, Trash2, Edit, X, Save, ExternalLink } from 'lucide-react';

export default function AdminLinks() {
  const [links, setLinks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await adminApi.getLinks();
      setLinks(res.data.results || res.data);
    } catch { toast.error('Error al cargar enlaces.'); }
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleSave = async () => {
    if (!modal?.name || !modal?.slug) {
      toast.error('Nombre y slug son obligatorios.');
      return;
    }
    try {
      if (modal.id) {
        await adminApi.updateLink(modal.id, modal);
      } else {
        await adminApi.createLink(modal);
      }
      toast.success('Enlace guardado.');
      setModal(null);
      load();
    } catch { toast.error('Error al guardar.'); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('¿Eliminar este enlace?')) return;
    try {
      await adminApi.deleteLink(id);
      toast.success('Enlace eliminado.');
      load();
    } catch { toast.error('Error al eliminar.'); }
  };

  return (
    <div>
      <div className="page-header">
        <h1>Enlaces Externos</h1>
        <p>Configura los botones de la pantalla principal</p>
      </div>

      <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 16 }}>
        <button className="btn btn--primary" onClick={() => setModal({ name: '', slug: '', url: '', icon_name: '', order: 0, is_active: true, show_in_repository: true })}>
          <Plus size={16} /> Nuevo enlace
        </button>
      </div>

      {loading ? <div className="spinner" /> : (
        <div className="card">
          {links.length === 0 ? (
            <div className="empty-state"><p>No hay enlaces configurados</p></div>
          ) : (
            <table className="data-table">
              <thead>
                <tr>
                  <th>Orden</th>
                  <th>Nombre</th>
                  <th>Slug</th>
                  <th>URL</th>
                  <th>Icono</th>
                  <th>Activo</th>
                  <th>En repositorio</th>
                  <th>Acciones</th>
                </tr>
              </thead>
              <tbody>
                {links.map(l => (
                  <tr key={l.id}>
                    <td>{l.order}</td>
                    <td style={{ fontWeight: 500 }}>{l.name}</td>
                    <td><code style={{ fontSize: '0.8rem', background: 'var(--gray-100)', padding: '2px 6px', borderRadius: 4 }}>{l.slug}</code></td>
                    <td style={{ maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {l.url ? (
                        <a href={l.url} target="_blank" rel="noreferrer" style={{ fontSize: '0.82rem', display: 'flex', alignItems: 'center', gap: 4 }}>
                          <ExternalLink size={12} /> {l.url}
                        </a>
                      ) : '—'}
                    </td>
                    <td>{l.icon_name || '—'}</td>
                    <td>
                      <span className={`badge ${l.is_active ? 'badge--success' : 'badge--neutral'}`}>
                        {l.is_active ? 'Sí' : 'No'}
                      </span>
                    </td>
                    <td>
                      <span className={`badge ${l.show_in_repository !== false ? 'badge--info' : 'badge--neutral'}`}>
                        {l.show_in_repository !== false ? 'Sí' : 'Solo en el alta'}
                      </span>
                    </td>
                    <td>
                      <div style={{ display: 'flex', gap: 4 }}>
                        <button className="btn btn--ghost btn--sm" onClick={() => setModal({ ...l })}><Edit size={14} /></button>
                        <button className="btn btn--ghost btn--sm" onClick={() => handleDelete(l.id)}><Trash2 size={14} color="var(--danger)" /></button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}

      {/* Modal */}
      {modal && (
        <div className="modal-overlay" onClick={() => setModal(null)}>
          <div className="modal" onClick={e => e.stopPropagation()}>
            <div className="modal__header">
              <h3>{modal.id ? 'Editar enlace' : 'Nuevo enlace'}</h3>
              <button className="btn btn--ghost btn--sm" onClick={() => setModal(null)}><X size={18} /></button>
            </div>
            <div className="form-row">
              <div className="form-group">
                <label>Nombre *</label>
                <input className="form-input" value={modal.name} onChange={e => setModal({ ...modal, name: e.target.value })} />
              </div>
              <div className="form-group">
                <label>Slug *</label>
                <input className="form-input" value={modal.slug} onChange={e => setModal({ ...modal, slug: e.target.value })} placeholder="identificador-unico" />
              </div>
            </div>
            <div className="form-group">
              <label>URL</label>
              <input className="form-input" value={modal.url} onChange={e => setModal({ ...modal, url: e.target.value })} placeholder="https://..." />
            </div>
            <div className="form-row">
              <div className="form-group">
                <label>Icono</label>
                <select className="form-input" value={modal.icon_name} onChange={e => setModal({ ...modal, icon_name: e.target.value })}>
                  <option value="">Seleccionar</option>
                  <option value="shirt">Shirt (Uniformes)</option>
                  <option value="link">Link</option>
                  <option value="calendar">Calendar</option>
                  <option value="settings">Settings</option>
                  <option value="clipboard">Clipboard</option>
                  <option value="graduation">Graduation</option>
                  <option value="book">Book</option>
                  <option value="info">Info</option>
                </select>
              </div>
              <div className="form-group">
                <label>Orden</label>
                <input className="form-input" type="number" value={modal.order} onChange={e => setModal({ ...modal, order: parseInt(e.target.value) || 0 })} />
              </div>
            </div>
            <div className="form-group">
              <label>
                <input type="checkbox" checked={modal.is_active} onChange={e => setModal({ ...modal, is_active: e.target.checked })} style={{ marginRight: 8 }} />
                Activo
              </label>
            </div>
            <div className="form-group">
              <label>
                <input type="checkbox" checked={modal.show_in_repository !== false}
                  onChange={e => setModal({ ...modal, show_in_repository: e.target.checked })} style={{ marginRight: 8 }} />
                Mostrar en el repositorio de enlaces
              </label>
              <p style={{ fontSize: '0.8rem', color: 'var(--gray-500)', marginTop: 4, marginLeft: 24 }}>
                Desactívalo para enlaces que solo se usan en un punto concreto, como el
                formulario de vacaciones del proceso de alta, y que no deben aparecer
                como botón en la pantalla de enlaces.
              </p>
            </div>
            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 16 }}>
              <button className="btn btn--secondary" onClick={() => setModal(null)}>Cancelar</button>
              <button className="btn btn--primary" onClick={handleSave}>
                <Save size={16} /> Guardar
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
